import { useState, useEffect, useRef, useCallback } from 'react';

type TabId = 'calendar' | 'heatmap';

const CALENDAR_SCRIPT = 'https://s3.tradingview.com/external-embedding/embed-widget-events.js';
const HEATMAP_SCRIPT = 'https://s3.tradingview.com/external-embedding/embed-widget-forex-heat-map.js';

const CALENDAR_CONFIG = {
  colorTheme: 'light',
  isTransparent: false,
  width: '100%',
  height: '100%',
  locale: 'en',
  importanceFilter: '-1,0,1',
};

const HEATMAP_CONFIG = {
  currencies: ['EUR', 'USD', 'JPY', 'GBP', 'CHF', 'AUD', 'CAD', 'NZD'],
  isTransparent: false,
  colorTheme: 'light',
  width: '100%',
  height: '100%',
  locale: 'en',
};

const SWIPE_THRESHOLD = 80;

function TradingViewEmbed({
  scriptSrc,
  config,
  className,
}: {
  scriptSrc: string;
  config: Record<string, unknown>;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.innerHTML = '';

    const widgetDiv = document.createElement('div');
    widgetDiv.className = 'tradingview-widget-container__widget';
    container.appendChild(widgetDiv);

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = scriptSrc;
    script.async = true;
    script.textContent = JSON.stringify(config);
    container.appendChild(script);

    return () => {
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [scriptSrc]);

  return (
    <div
      ref={containerRef}
      className={`tradingview-widget-container ${className || ''}`}
    />
  );
}

export default function MarketToolsPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('calendar');
  const [touchOffset, setTouchOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const touchStartXRef = useRef(0);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    setIsSwiping(false);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const delta = e.touches[0].clientX - touchStartXRef.current;
    if (delta > 0) {
      setIsSwiping(true);
      setTouchOffset(delta);
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    setIsSwiping(false);
    if (touchOffset > SWIPE_THRESHOLD) {
      setIsOpen(false);
    }
    setTouchOffset(0);
  }, [touchOffset]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      {/* Floating Toggle Button */}
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className={`fixed z-40 cursor-pointer shadow-lg transition-all duration-300 group
          left-4 bottom-6 md:left-auto md:right-0 md:top-1/2 md:-translate-y-1/2
          rounded-full md:rounded-l-xl md:rounded-r-none
          flex items-center justify-center md:flex-col
          w-11 h-11 md:w-auto md:h-auto
          md:px-2.5 md:py-5 md:gap-1.5
          ${isOpen
            ? 'bg-foreground-800 text-white opacity-0 pointer-events-none'
            : 'bg-primary-500 text-white hover:bg-primary-600'
          }`}
        aria-label="Open Market Tools"
      >
        <i className="ri-line-chart-line text-base md:text-lg"></i>
        <span
          className="hidden md:inline text-[13px] font-semibold tracking-wider whitespace-nowrap"
          style={{ writingMode: 'vertical-rl' }}
        >
          Market Tools
        </span>
      </button>

      {/* Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-black/25 transition-opacity duration-500 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsOpen(false)}
      />

      {/* Slide-out Panel */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className={`fixed top-0 right-0 z-50 h-full w-full sm:w-[450px] bg-white shadow-2xl flex flex-col ${
          isSwiping ? '' : 'transition-transform duration-500 ease-out'
        } ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={
          isOpen && touchOffset > 0
            ? { transform: `translateX(${touchOffset}px)` }
            : undefined
        }
      >
        {/* Panel Header */}
        <div className="flex items-center justify-between px-4 md:px-5 py-3 md:py-4 border-b border-background-200 flex-shrink-0">
          <div className="flex items-center gap-2 md:gap-2.5">
            <span className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-green-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.5)]"></span>
            <span className="text-[14px] md:text-[15px] font-bold text-foreground-950 whitespace-nowrap">
              Live Market Data
            </span>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="Close panel"
          >
            <i className="ri-close-line text-lg text-foreground-600"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 md:gap-1.5 px-3 md:px-5 py-2.5 md:py-3 border-b border-background-100 flex-shrink-0">
          <button
            onClick={() => setActiveTab('calendar')}
            className={`px-3 md:px-4 py-1.5 md:py-2 text-[12px] md:text-[13px] font-semibold rounded-full whitespace-nowrap transition-all cursor-pointer ${
              activeTab === 'calendar'
                ? 'bg-primary-500 text-white shadow-[0_2px_8px_rgba(0,0,0,0.12)]'
                : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
            }`}
          >
            <i className="ri-calendar-event-line mr-1 md:mr-1.5"></i>
            Economic Calendar
          </button>
          <button
            onClick={() => setActiveTab('heatmap')}
            className={`px-3 md:px-4 py-1.5 md:py-2 text-[12px] md:text-[13px] font-semibold rounded-full whitespace-nowrap transition-all cursor-pointer ${
              activeTab === 'heatmap'
                ? 'bg-primary-500 text-white shadow-[0_2px_8px_rgba(0,0,0,0.12)]'
                : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
            }`}
          >
            <i className="ri-fire-line mr-1 md:mr-1.5"></i>
            Forex Heat Map
          </button>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-hidden relative">
          {activeTab === 'calendar' && (
            <div className="absolute inset-0 overflow-auto">
              <TradingViewEmbed
                scriptSrc={CALENDAR_SCRIPT}
                config={CALENDAR_CONFIG}
                className="w-full min-h-full"
              />
            </div>
          )}
          {activeTab === 'heatmap' && (
            <div className="absolute inset-0 overflow-auto">
              <TradingViewEmbed
                scriptSrc={HEATMAP_SCRIPT}
                config={HEATMAP_CONFIG}
                className="w-full min-h-full"
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}